import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  doc,
} from "firebase/firestore";
import db from "./FirestoreConfig";
import { runInAction } from "mobx";
import ChatManager from "../Classes/ChatManager";
import {
  hasCompetitionIdInQueue,
  shouldProcessCompetitionSnapshot,
  shouldRemoveLocalCompetitionOnMissingSnapshot,
} from "../utils/competitionSyncGuards";

class ClientFirestoreListener {
  arraysListener = null;
  formsListener = null;
  blocksListener = null;
  competitionsListener = null;
  competitionsListenerState = null;
  chatListener = null;

  constructor(client) {
    this.client = client;
  }

  /* ARRAYS LISTENER */
  async setupArraysListener() {
    return new Promise((resolve, reject) => {
      let isFirstExecution = true;

      try {
        if (this.arraysListener) {
          console.log("Removing old arrays listener...");
          this.removeArraysListener();
        }

        console.log("Setting up arrays listener...");

        // Listen to a single document, not a collection
        const arraysDocRef = doc(
          db,
          "users",
          this.client.id,
          "arrays",
          "arrays"
        );

        this.arraysListener = onSnapshot(
          arraysDocRef,
          (docSnapshot) => {
            try {
              if (docSnapshot.exists()) {
                const arraysData = docSnapshot.data();
                console.log("Document arrays added/modified:");
                this.fetchRealTimeArrays(arraysData);
              }

              if (isFirstExecution) {
                isFirstExecution = false;
                resolve({ success: true });
              }
            } catch (error) {
              console.error("Error processing document snapshot:", error);
              if (isFirstExecution) {
                isFirstExecution = false;
                reject(error);
              }
            }
          },
          (error) => {
            console.error("Error with arrays listener:", error);
            if (error.code === "permission-denied") {
              console.log("Permission error, stopping arrays listener.");
              this.removeArraysListener();
            }
            if (isFirstExecution) {
              isFirstExecution = false;
              reject(error);
            }
          }
        );
      } catch (error) {
        console.error("Error setting up arrays listener:", error);
        if (isFirstExecution) {
          reject(error);
        }
      }
    });
  }
  removeArraysListener() {
    if (this.arraysListener) {
      console.log("Removing arrays listener...");
      this.arraysListener();
      this.arraysListener = null;
    } else {
      console.log("No active arrays listener to remove.");
    }
  }

  /* FORMS LISTENER */
  setupFormsListener() {
    return new Promise((resolve, reject) => {
      let isFirstExecution = true;

      const twoWeekAgo = new Date();
      twoWeekAgo.setDate(twoWeekAgo.getDate() - 14);

      try {
        if (this.formsListener) {
          console.log("Removing old forms listener...");
          this.removeFormsListener();
        }

        console.log("Setting up forms listener...");

        const formsQuery = query(
          collection(db, "users", this.client.id, "forms"),
          where("last_update", ">=", twoWeekAgo)
        );

        this.formsListener = onSnapshot(
          formsQuery,
          (querySnapshot) => {
            try {
              runInAction(() => {
                querySnapshot.docChanges().forEach((change) => {
                  if (change.type === "added" || change.type === "modified") {
                    // console.log(
                    //   "Document added/modified:",
                    //   change.doc.data().name
                    // );
                    this.client.fetchRealTimeFormData(change.doc.data());
                  }
                });

                if (isFirstExecution) {
                  isFirstExecution = false;
                  resolve({ success: true });
                }
              });
            } catch (error) {
              console.error("Error processing snapshot:", error);
              if (isFirstExecution) {
                isFirstExecution = false;
                reject(error);
              }
            }
          },
          (error) => {
            console.error("Error with forms listener:", error);
            // crashlytics().recordError(error);
            if (error.code === "permission-denied") {
              console.log("Permission error, stopping forms listener.");
              this.removeFormsListener();
            }
            if (isFirstExecution) {
              isFirstExecution = false;
              reject(error);
            }
          }
        );
      } catch (error) {
        console.error("Error setting up forms listener:", error);
        if (isFirstExecution) {
          reject(error);
        }
      }
    });
  }

  removeFormsListener() {
    if (this.formsListener) {
      console.log("Removing forms listener...");
      this.formsListener();
      this.formsListener = null;
    } else {
      console.log("No active forms listener to remove.");
    }
  }

  /* BLOCKS LISTENER */
  async setupBlocksListener(oneWeek = false) {
    return new Promise((resolve, reject) => {
      let isFirstExecution = true;

      try {
        // Remove the existing blocks listener if it exists
        if (this.blocksListener) {
          console.log("Removing old blocks listener...");
          this.blocksListener();
          this.blocksListener = null;
        }

        console.log(
          `Setting up blocks listener for documents from the last ${
            oneWeek ? "week" : "three months"
          }...`
        );

        const cutoffDate = new Date();
        if (oneWeek) {
          cutoffDate.setDate(cutoffDate.getDate() - 7);
        } else {
          cutoffDate.setMonth(cutoffDate.getMonth() - 3);
        }

        // Calculate the date for cutoff
        const blocksCollectionRef = query(
          collection(db, "users", this.client.id, "blocks"),
          where("last_update", ">=", cutoffDate)
        );

        // Set up the listener
        this.blocksListener = onSnapshot(
          blocksCollectionRef,
          { includeMetadataChanges: false },
          (querySnapshot) => {
            try {
              runInAction(() => {
                querySnapshot.docChanges().forEach((change) => {
                  const blockData = change.doc.data();

                  if (change.type === "added" || change.type === "modified") {
                    if (blockData.modified_by !== this.client.coach.sessionId) {
                      // console.log("Document added/modified:", blockData.name);
                      this.client.fetchRealTimeBlockData(blockData);
                    }
                  } else if (change.type === "removed") {
                    console.log("Document removed:", blockData.name);
                    const blockIndex = this.client.blocks.findIndex(
                      (block) =>
                        block.id.toString().toLowerCase() === change.doc.id
                    );

                    if (blockIndex !== -1) {
                      this.client.blocks.splice(blockIndex, 1); // Remove the block from the array
                    }
                  }
                });
              });
              // Resolve the promise on the first successful execution
              if (isFirstExecution) {
                isFirstExecution = false;
                resolve({ success: true });
              }
            } catch (error) {
              console.error("Error processing snapshot:", error);
              if (isFirstExecution) {
                isFirstExecution = false;
                reject(error);
              }
            }
          },
          (error) => {
            console.error("Error with blocks listener:", error);
            if (error.code === "permission-denied") {
              console.log("Permission error, listener will stop.");
              if (this.blocksListener) {
                this.blocksListener();
                this.blocksListener = null;
              }
            }
            if (isFirstExecution) {
              isFirstExecution = false;
              reject(error);
            }
          }
        );
      } catch (error) {
        console.error("Error setting up blocks listener:", error);
        if (isFirstExecution) {
          reject(error);
        }
      }
    });
  }

  removeBlocksListener() {
    if (this.blocksListener) {
      console.log("Removing blocks listener...");
      this.blocksListener();
      this.blocksListener = null;
    } else {
      console.log("No active blocks listener to remove.");
    }
  }

  /* COMPETITIONS LISTENER */
  setupCompetitionsListener(competitionId) {
    console.log("Setting up competitions listener for", competitionId);

    try {
      if (this.competitionsListener) {
        console.log("Removing old competitions listener...");
        this.removeCompetitionsListener();
      }

      const competitionDocRef = doc(
        db,
        "users",
        this.client.id,
        "competitions",
        competitionId
      );
      this.competitionsListenerState = {
        competitionId,
        hasSeenExistingSnapshot: false,
      };

      this.competitionsListener = onSnapshot(
        competitionDocRef,
        (docSnapshot) => {
          try {
            runInAction(() => {
              const normalizedCompetitionId = competitionId.toString().toLowerCase();
              const hasLocalCompetition =
                this.client.competitions.findIndex(
                  (competition) =>
                    competition.id.toString().toLowerCase() ===
                    normalizedCompetitionId
                ) !== -1;

              if (docSnapshot.exists()) {
                if (this.competitionsListenerState) {
                  this.competitionsListenerState.hasSeenExistingSnapshot = true;
                }
                const competitionData = docSnapshot.data();

                if (
                  shouldProcessCompetitionSnapshot({
                    competitionData,
                    currentSessionId: this.client.coach.sessionId,
                    hasLocalCompetition,
                  })
                ) {
                  console.log("Document updateddddd:", competitionData.name);
                  this.client.fetchRealTimeCompetitionData(competitionData);
                }
              } else {
                const hasPendingCompetitionWrite = hasCompetitionIdInQueue(
                  this.client.clientTransactionCompetitions,
                  competitionId
                );
                const hasPendingCompetitionDeletion = hasCompetitionIdInQueue(
                  this.client.clientDeletionCompetitions,
                  competitionId
                );
                const removalDecision =
                  shouldRemoveLocalCompetitionOnMissingSnapshot({
                    hasSeenExistingSnapshot:
                      this.competitionsListenerState?.hasSeenExistingSnapshot ===
                      true,
                    hasPendingCompetitionWrite,
                    hasPendingCompetitionDeletion,
                  });

                console.log("Document removed:", competitionId);
                console.log("Competition missing snapshot decision:", {
                  competitionId,
                  hasPendingCompetitionWrite,
                  hasPendingCompetitionDeletion,
                  hasSeenExistingSnapshot:
                    this.competitionsListenerState?.hasSeenExistingSnapshot ===
                    true,
                  shouldRemove: removalDecision.shouldRemove,
                  reason: removalDecision.reason,
                });

                if (removalDecision.shouldRemove) {
                  const index = this.client.competitions.findIndex(
                    (competition) =>
                      competition.id.toString().toLowerCase() ===
                      normalizedCompetitionId
                  );
                  if (index !== -1) {
                    this.client.competitions.splice(index, 1);
                    console.log("Removed competition locally", competitionId);
                  }
                } else {
                  console.log(
                    "Skipping local competition removal for missing snapshot",
                    competitionId
                  );
                }
              }
            });
          } catch (error) {
            console.error("Error processing snapshot:", error);
            // throw error;
          }
        },
        (error) => {
          console.error("Error with competitions listener:", error);
          //   crashlytics().recordError(error);
          if (error.code === "permission-denied") {
            console.log("Permission error, stopping competitions listener.");
            this.removeCompetitionsListener();
          }
        }
      );
    } catch (error) {
      console.error("Error setting up competitions listener:", error);
      // throw error; //Uncomment this line to show error in case of competition listener failure
    }
  }

  removeCompetitionsListener() {
    if (this.competitionsListener) {
      console.log("Removing competitions listener...");
      this.competitionsListener();
      this.competitionsListener = null;
      this.competitionsListenerState = null;
    } else {
      console.log("No active competitions listener to remove.");
      this.competitionsListenerState = null;
    }
  }

  /* CHAT LISTENER */
  async setupChatListener(t) {
    return new Promise((resolve, reject) => {
      let isFirstExecution = true;

      const client = this.client;

      if (!client) {
        console.warn("No client found");
        return reject(new Error(`No client found`));
      }

      // Remove the existing chat listener if it exists
      if (this.chatListener) {
        console.log("Removing old chat listener...");
        this.chatListener();
        this.chatListener = null;
      }

      console.log("Setting up chat listener...");

      // Initialize messageIds set with existing messages
      client.messageIds.clear();
      client.messages.forEach((msg) =>
        client.messageIds.add(String(msg.id).toLowerCase())
      );

      const chatDocumentRef = doc(
        db,
        "coaches",
        client.coach.id,
        "chats",
        client.id,
        "batched_messages",
        client.id
      );

      // Set up the listener
      this.chatListener = onSnapshot(
        chatDocumentRef,
        { includeMetadataChanges: false },
        (snap) => {
          try {
            if (!snap.exists) {
              console.log("Document does not exist:", chatDocumentRef.path);
              if (isFirstExecution) {
                isFirstExecution = false;
                resolve({ success: true });
              }
              return;
            }

            const batchData = snap.data();
            const batchMessages = batchData?.messages || [];
            if (batchMessages.length === 0) {
              console.log("No messages in document:", chatDocumentRef.path);
              if (isFirstExecution) {
                isFirstExecution = false;
                resolve({ success: true });
              }
              return;
            }
            const newMessages = batchMessages.filter(
              (md) => !client.messageIds.has(String(md.id).toLowerCase())
            );

            const firstTimestampFromBatch =
              batchMessages.length > 0
                ? batchMessages[0].timestamp.toDate()
                : null;

            // Collect block IDs: on first run use all messages, afterwards just the new ones
            const relevantMessages = isFirstExecution
              ? batchMessages
              : newMessages;
            const blockIdsOfRecentMessages = new Set(
              relevantMessages.map((md) => md.block_id).filter(Boolean)
            );

            runInAction(() => {
              // Only clean up old messages on first execution
              if (
                batchMessages.length > 80 &&
                firstTimestampFromBatch &&
                isFirstExecution
              ) {
                const firestoreIDs = new Set(
                  batchMessages.map((m) => String(m.id).toLowerCase())
                );
                const newMessages = client.messages.filter((msg) => {
                  const msgTimestamp =
                    msg.timestamp instanceof Date
                      ? msg.timestamp
                      : new Date(msg.timestamp);

                  // KEEP messages that are still in Firestore OR newer than the cutoff
                  return (
                    firestoreIDs.has(String(msg.id).toLowerCase()) ||
                    msgTimestamp > firstTimestampFromBatch
                  );
                });
                client.setProperty("messages", newMessages);
              }

              // Fetch any new messages
              newMessages.forEach((md) => {
                client.fetchMessage(md);
                client.messageIds.add(String(md.id).toLowerCase());
              });

              if (newMessages.length > 0) {
                // client.refreshed = !client.refreshed;
                const chatManager = ChatManager.getInstance();

                setTimeout(() => {
                  chatManager.scrollToBottom();
                }, 200);
              }
            });

            if (blockIdsOfRecentMessages.size) {
              client.fetchMissingBlocksFromIDsArray(
                Array.from(blockIdsOfRecentMessages),
                t
              );
            }
            if (isFirstExecution) {
              isFirstExecution = false;
              resolve({ success: true });
            }
          } catch (error) {
            console.error("Error processing snapshot:", error);
            if (isFirstExecution) {
              reject(error);
            }
            isFirstExecution = false;
          }
        },
        (error) => {
          console.error("Error with chat listener:", error);
          // recordError(crashlytics, error);
          if (error.code === "permission-denied") {
            console.log("Permission error, listener will stop.");
            if (this.chatListener) {
              this.chatListener();
              this.chatListener = null;
            }
          }
          if (isFirstExecution) {
            reject(error);
          }
          isFirstExecution = false;
        }
      );
    });
  }

  removeChatListener() {
    if (this.chatListener) {
      console.log("Removing chat listener...");
      this.chatListener();
      this.chatListener = null;
    } else {
      console.log("No active chat listener to remove.");
    }
  }
}

export default ClientFirestoreListener;
